// ─────────────────────────────────────────────────────────────────────────────
// SERPS Design Tokens — Brunei National Education Design System
// These override the shared ui-kit tokens for the web-portal.
// ─────────────────────────────────────────────────────────────────────────────

export const T = {
  // ── Colour ──────────────────────────────────────────────────────────────────
  color: {
    // Primary — Brunei Royal
    royal:       '#F7D000',
    royalDeep:   '#C9A800',
    royalPale:   '#FFFAED',
    ivory:       '#FAF8F2',

    // Dark palette (sidebar, authority surfaces)
    midnight:    '#0F1117',
    charcoal:    '#1E2128',
    slate:       '#2C3038',
    steel:       '#4A5568',

    // Text — CSS vars switch with data-theme
    textPrimary:   'var(--color-text-primary)',
    textSecondary: 'var(--color-text-secondary)',
    textDisabled:  'var(--color-text-disabled)',
    textInverse:   '#F9FAFB',

    // Semantic status — fixed regardless of theme
    success:      '#0D7A4E',
    successLight: 'var(--color-success-light)',
    warning:      '#D97706',
    warningLight: 'var(--color-warning-light)',
    danger:       '#B91C1C',
    dangerLight:  'var(--color-danger-light)',
    info:         '#1D4ED8',
    infoLight:    'var(--color-info-light)',

    // Surfaces — CSS vars switch with data-theme
    canvas:       'var(--color-canvas)',
    card:         'var(--color-card)',
    cardAlt:      'var(--color-card-alt)',
    hoverRow:     'var(--color-hover-row)',

    // Borders — CSS vars switch with data-theme
    border:       'var(--color-border)',
    borderSubtle: 'var(--color-border-subtle)',
    borderStrong: 'var(--color-border-strong)',
  },

  // ── Typography ───────────────────────────────────────────────────────────────
  font: {
    serif: '"Noto Serif", Georgia, serif',
    sans:  '"Noto Sans", -apple-system, sans-serif',
    jawi:  '"Noto Naskh Arabic", serif',
    mono:  '"JetBrains Mono", "Courier New", monospace',
  },

  size: {
    xs:   '11px',
    sm:   '12px',
    base: '13px',
    md:   '14px',
    lg:   '16px',
    xl:   '20px',
    '2xl':'25px',
    '3xl':'32px',
    '4xl':'40px',
  },

  weight: {
    normal:   400,
    medium:   500,
    semibold: 600,
    bold:     700,
  },

  // ── Spacing (4px base unit) ───────────────────────────────────────────────
  sp: {
    '1':  '4px',
    '2':  '8px',
    '3':  '12px',
    '4':  '16px',
    '5':  '20px',
    '6':  '24px',
    '8':  '32px',
    '10': '40px',
    '12': '48px',
    '16': '64px',
  },

  // ── Shadows ──────────────────────────────────────────────────────────────────
  shadow: {
    xs: '0 1px 2px rgba(0,0,0,0.05)',
    sm: '0 1px 3px rgba(0,0,0,0.08),0 1px 2px rgba(0,0,0,0.04)',
    md: '0 4px 6px rgba(0,0,0,0.06),0 2px 4px rgba(0,0,0,0.04)',
    lg: '0 10px 15px rgba(0,0,0,0.08),0 4px 6px rgba(0,0,0,0.04)',
    xl: '0 20px 25px rgba(0,0,0,0.10),0 8px 10px rgba(0,0,0,0.04)',
  },

  // ── Border radius ────────────────────────────────────────────────────────────
  radius: {
    sm:   '4px',
    md:   '6px',
    lg:   '8px',
    xl:   '12px',
    '2xl':'16px',
    full: '999px',
  },

  // ── Layout ───────────────────────────────────────────────────────────────────
  layout: {
    sidebarW:    '256px',
    sidebarCollapsed: '68px',
    navbarH:     '60px',
    contentMax:  '1280px',
    contentPad:  '32px',
  },
} as const;

// ── Status pill config ────────────────────────────────────────────────────────
export const STATUS_PILLS = {
  HADIR:          { ms: 'Hadir',            en: 'Present',  bg: '#ECFDF5', color: '#0D7A4E' },
  LEWAT:          { ms: 'Lewat',            en: 'Late',     bg: '#FFFBEB', color: '#D97706' },
  TIDAK_HADIR:    { ms: 'Tidak Hadir',      en: 'Absent',   bg: '#FEF2F2', color: '#B91C1C' },
  ABSENT_SICK:    { ms: 'Sakit',            en: 'Sick',     bg: '#EFF6FF', color: '#1D4ED8' },
  ABSENT_EXCUSED: { ms: 'Cuti Diluluskan',  en: 'Excused',  bg: '#FFFBEB', color: '#D97706' },
  AKTIF:          { ms: 'Aktif',            en: 'Active',   bg: '#ECFDF5', color: '#0D7A4E' },
  TIDAK_AKTIF:    { ms: 'Tidak Aktif',      en: 'Inactive', bg: '#F3F4F6', color: '#4A5568' },
  TERTANGGUH:     { ms: 'Tertangguh',       en: 'Pending',  bg: '#FFFBEB', color: '#D97706' },
  DITOLAK:        { ms: 'Ditolak',          en: 'Rejected', bg: '#FEF2F2', color: '#B91C1C' },
  DILULUSKAN:     { ms: 'Diluluskan',       en: 'Approved', bg: '#ECFDF5', color: '#0D7A4E' },
  DALAM_SEMAKAN:  { ms: 'Dalam Semakan',    en: 'In Review',bg: '#EFF6FF', color: '#1D4ED8' },
  DRAF:           { ms: 'Draf',             en: 'Draft',    bg: '#F3F4F6', color: '#4A5568' },
  DIHANTAR:       { ms: 'Dihantar',         en: 'Submitted',bg: '#EFF6FF', color: '#1D4ED8' },
} as const;

export type StatusKey = keyof typeof STATUS_PILLS;

// Role pill colours for sidebar
export const ROLE_PILL_COLORS: Record<string, { bg: string; color: string; label_ms: string; label_en: string }> = {
  super_admin:     { bg: '#1E2128', color: '#F7D000', label_ms: 'Pentadbir Super',   label_en: 'Super Admin'   },
  moe_admin:       { bg: '#1D4ED8', color: '#FFFFFF', label_ms: 'Pegawai KPM',       label_en: 'MOE Officer'   },
  admin:           { bg: '#F7D000', color: '#111827', label_ms: 'Pentadbir Sekolah', label_en: 'School Admin'  },
  principal:       { bg: '#F7D000', color: '#111827', label_ms: 'Pengetua',           label_en: 'Principal'     },
  teacher:         { bg: '#0D7A4E', color: '#FFFFFF', label_ms: 'Guru',               label_en: 'Teacher'       },
  clerk:           { bg: '#4A5568', color: '#FFFFFF', label_ms: 'Kerani',             label_en: 'Clerk'         },
  parent:          { bg: '#7C3AED', color: '#FFFFFF', label_ms: 'Ibu Bapa / Penjaga', label_en: 'Parent'        },
  dpe_officer:     { bg: '#B91C1C', color: '#FFFFFF', label_ms: 'Pegawai DPE',        label_en: 'DPE Officer'   },
  mora_officer:    { bg: '#0D7A4E', color: '#FFFFFF', label_ms: 'Pegawai MORA',       label_en: 'MORA Officer'  },
  sen_coordinator: { bg: '#D97706', color: '#FFFFFF', label_ms: 'Koordinator SEN',    label_en: 'SEN Coord.'    },
};
